import React, { useState, useEffect } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  TextField, Button, Checkbox, Card, Snackbar, Alert, Fade, Slide, Collapse,
  Box, Chip, Paper, Typography, Grid, Container
} from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ============================================
// HELPER FUNCTIONS
// ============================================

const roundSmallValues = (hours) => {
  return hours < 0.01 ? 0.00 : parseFloat(hours.toFixed(2));
};

// ✅ NEW: Helper function to format non-job activities with enhanced readability
const formatNonJobActivities = (activitiesString) => {
  if (!activitiesString) return '';

  // Split by comma to get individual activities
  const activities = activitiesString.split(',').map(activity => activity.trim()).filter(Boolean);

  return activities.map(activity => {
    // Extract activity description, start time, and end time
    // Pattern: "Activity Description YYYY-MM-DD HH:MM AM/PM-YYYY-MM-DD HH:MM AM/PM"
    const match = activity.match(/^(.*?)\s+(\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}\s+[AP]M)-(\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}\s+[AP]M)$/);

    if (match) {
      const [, description, startDateTime, endDateTime] = match;

      const startDate = startDateTime.split(' ')[0]; // 2025-12-30
      const startTime = startDateTime.split(' ').slice(1).join(' '); // 12:34 PM

      const endDate = endDateTime.split(' ')[0]; // 2025-12-30  
      const endTime = endDateTime.split(' ').slice(1).join(' '); // 01:15 PM

      // Case 1: Same date
      if (startDate === endDate) {
        // Case 1a: Same time (very short duration)
        if (startTime === endTime) {
          return `${description} ${startDate} (${startTime})`;
        }
        // Case 1b: Same date, different times
        else {
          return `${description} ${startDate} (${startTime} to ${endTime})`;
        }
      }
      // Case 2: Different dates (overnight activity)
      else {
        return `${description} ${startDate} ${startTime} to ${endDate} ${endTime}`;
      }
    }

    // Fallback: return original if parsing fails
    return activity;
  }).join('\n'); // Use line breaks to separate activities
};



// ✅ UPDATED: Helper function to return decimal hours as NUMBER (not string)
const formatDecimalHours = (decimalHours) => {
  if (!decimalHours || decimalHours === 0) return 0;

  // Return as number with 2 decimal places
  return parseFloat(parseFloat(decimalHours).toFixed(2));
};



const convertTimeToDecimalHours = (timeStr) => {
  if (!timeStr) return 0;

  // If it's already a number, return it
  if (typeof timeStr === 'number') return timeStr;

  // Convert string to string to handle any format
  const timeString = timeStr.toString();

  // Handle HH:MM:SS format
  if (timeString.includes(':')) {
    const parts = timeString.split(':');
    if (parts.length === 3) {
      const hours = parseFloat(parts[0]) || 0;
      const minutes = parseFloat(parts[1]) || 0;
      const seconds = parseFloat(parts[2]) || 0;
      return hours + (minutes / 60) + (seconds / 3600);
    }
  }

  // If it's already decimal format, return as number
  return parseFloat(timeString) || 0;
};


const formatEmployees = (operator, astOperator, helpers) => {
  let employeesList = [];
  if (operator) {
    employeesList.push(...operator.split(",").map(name => name.trim() + "[OP]"));
  }
  if (astOperator) {
    employeesList.push(...astOperator.split(",").map(name => name.trim() + "[AOP]"));
  }
  if (helpers) {
    employeesList.push(...helpers.split(",").map(name => name.trim() + "[HLP]"));
  }
  return employeesList.join(", ");
};

const convertTimeToSeconds = (timeStr) => {
  if (!timeStr) return 0;
  if (typeof timeStr !== "string") return Number(timeStr);
  if (!timeStr.includes(":")) return Number(timeStr);
  const parts = timeStr.split(':');
  if (parts.length !== 3) return 0;
  const hours = parseFloat(parts[0]) || 0;
  const minutes = parseFloat(parts[1]) || 0;
  const seconds = parseFloat(parts[2]) || 0;
  return hours * 3600 + minutes * 60 + seconds;
};

// ✅ SIMPLIFIED: Process the pre-combined employee activities data from backend JOIN
const processEmployeeActivitiesData = (employeeActivitiesData) => {


  // ✅ Simple mapping - data is already combined by backend JOIN
  const result = employeeActivitiesData.map((employee, idx) => ({
    "No:": idx + 1,
    "emp_id": employee.emp_id,
    "emp_name": employee.emp_name,
    "shift_in_datetime": employee.shift_in_datetime ? dayjs(employee.shift_in_datetime).format("YYYY-MM-DD hh:mm A") : '',
    "shift_out_datetime": employee.shift_out_datetime ? dayjs(employee.shift_out_datetime).format("YYYY-MM-DD hh:mm A") : '',
    "non_job_activities": formatNonJobActivities(employee.non_job_activities || ''),

    "remarks": employee.shift_remarks || ''
  }));

  return result;
};

// ✅ LEGACY: Keep the old complex function for backward compatibility (can be removed later)
const combineEmployeeActivities = (shiftsData, opelogData) => {

  const employeeMap = new Map();

  // Process shift data to create employee records
  if (shiftsData && shiftsData.length > 0) {


    shiftsData.forEach((shift, index) => {
      const empId = shift.emp_id;

      if (!employeeMap.has(empId)) {
        employeeMap.set(empId, {
          emp_id: empId,
          emp_name: shift.emp_name || '',
          shift_in_datetime: shift.shift_in_datetime,
          shift_out_datetime: shift.shift_out_datetime,
          shift_remarks: shift.remarks || '',
          non_job_activities: []
        });
      }
    });
  }

  // Process operation log data to add non-job activities
  if (opelogData && opelogData.length > 0) {
    opelogData.forEach((opelog, index) => {
      let empId = opelog.emp_id;

      // Try to find matching employee
      if (empId && employeeMap.has(empId)) {
        const employee = employeeMap.get(empId);
        const activityText = `${opelog.operation_description || 'No Work'} ${dayjs(opelog.start_datetime).format('YYYY-MM-DD hh:mm A')}-${dayjs(opelog.end_datetime).format('YYYY-MM-DD hh:mm A')}`;
        employee.non_job_activities.push(activityText);
      }
    });
  }

  // Convert to Excel format
  return Array.from(employeeMap.values()).map((employee, idx) => ({
    "No:": idx + 1,
    "emp_id": employee.emp_id,
    "emp_name": employee.emp_name,
    "shift_in_datetime": employee.shift_in_datetime ? dayjs(employee.shift_in_datetime).format("YYYY-MM-DD hh:mm A") : '',
    "shift_out_datetime": employee.shift_out_datetime ? dayjs(employee.shift_out_datetime).format("YYYY-MM-DD hh:mm A") : '',
    "non_job_activities": formatNonJobActivities(employee.non_job_activities.join(', ')),

    "remarks": employee.shift_remarks || ''
  }));
};

// ============================================
// EXCEL HEADERS
// ============================================

const filteredPlanHeaders = [
  "No:", "JobNumber", "Product", "Customer", "WOQty", "OPQty", "PlannedQty",
  "PlannedStatus", "ActualProductionQty", "MainOperation",
  "PlanSetupTime(h)", "OperationalTime(h)", "SetupAndOpeTime(h)", "TotalSetupTime(h)",
  "ActualRunningTime(h)", "TotalActualTime(h)", "TimeUnit", "WOduedate", "IdleReasons", "JobCloseRemarks"
];

// ✅ NEW: Headers for QCM group (with PassedQty instead of ActualProductionQty)
const qcmPlanHeaders = [
  "No:", "JobNumber", "Product", "Customer", "WOQty", "OPQty", "PlannedQty",
  "PlannedStatus", "PassedQty", "MainOperation",
  "PlanSetupTime(h)", "OperationalTime(h)", "SetupAndOpeTime(h)", "TotalSetupTime(h)",
  "ActualRunningTime(h)", "TotalActualTime(h)", "TimeUnit", "WOduedate", "IdleReasons", "JobCloseRemarks"
];
const filteredShiftHeaders = [
  "No:", "shift_loggedIn_DateTime", "shift_loggedOut_DateTime", "Employees", "remarks"
];


const manHourPlanHeaders = [
  "No:", "JobNumber", "Product", "Customer", "MainOperation",
  "PlannedQty", "PlannedStatus",
  "ActualProductionQty",
  "PlannedSetupTime", "PlannedOperationalTime", "PlannedSetup+OperationalTime",
  "ActualSetupTime", "ActualRunningTime", "TotalProdTime", "TimeUnit", // ✅ Added two new columns
  "EmployeeList"
];


// ✅ NEW: Combined Employee Activities Headers
const combinedEmployeeActivitiesHeaders = [
  "No:", "emp_id", "emp_name", "shift_in_datetime", "shift_out_datetime", "non_job_activities", "remarks"
];

export default function PlanVsActualFilter() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [data, setData] = useState({});
  const [selectedItems, setSelectedItems] = useState({});
  const [isDownloadVisible, setIsDownloadVisible] = useState(false);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [showWorkstations, setShowWorkstations] = useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [workstationTypes, setWorkstationTypes] = useState({});
  const [hoursType, setHoursType] = useState('');
  const [isLoadingMachineHour, setIsLoadingMachineHour] = useState(false);
  const [isLoadingManHour, setIsLoadingManHour] = useState(false);
  const [machineHourCount, setMachineHourCount] = useState(0);
  const [manHourCount, setManHourCount] = useState(0);

  const [alert, setAlert] = useState({ open: false, severity: "info", message: "" });

  useEffect(() => {
    const initialSelection = {};
    Object.entries(data).forEach(([group, machines]) => {
      initialSelection[group] = {};
      machines.forEach(machine => {
        initialSelection[group][machine] = true;
      });
    });
    setSelectedItems(initialSelection);
    setIsDownloadVisible(Object.keys(initialSelection).length > 0);
  }, [data]);

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") return;
    setAlert(prev => ({ ...prev, open: false }));
  };

  // ✅ OPTIMIZED: Compact generateMachineHourGroupedSheet with QCM support
  const generateMachineHourGroupedSheet = (workbook, groupCode, groupData, selectedDate) => {
    const sheet = workbook.addWorksheet(`${groupCode}_PlanVsActual`);
    const fullOpelogSheet = workbook.addWorksheet(`${groupCode}_FullOpeLog`);

    // ✅ NEW: Determine if this is QCM group
    const isQCMGroup = groupCode === 'QCM';
    const dynamicPlanHeaders = isQCMGroup ? qcmPlanHeaders : filteredPlanHeaders;

    let currentRow = 1;

    // Group title - STEEL BLUE THEME (compact)
    const groupTitleText = `${groupCode} Group - Machine-Hour Plan Vs Actual - ${selectedDate.format("YYYY-MM-DD")}`;
    sheet.addRow([groupTitleText]);
    sheet.mergeCells(currentRow, 1, currentRow, 19);
    const groupTitleCell = sheet.getCell(currentRow, 1);
    groupTitleCell.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
    groupTitleCell.alignment = { horizontal: "center", vertical: "middle" };
    groupTitleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4682B4" } };
    groupTitleCell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" }
    };
    sheet.getRow(currentRow).height = 25;
    currentRow++;

    // ✅ OPTIMIZED: Process each workstation with minimal spacing
    groupData.forEach(({ code, data, fullOpelog, nonJobData }, index) => {


      // Workstation title - LIGHT STEEL BLUE (compact)
      sheet.addRow([`📍 ${code}`]);
      sheet.mergeCells(currentRow, 1, currentRow, 19);
      const stationTitleCell = sheet.getCell(currentRow, 1);
      stationTitleCell.font = { bold: true, size: 12 };
      stationTitleCell.alignment = { horizontal: "left", vertical: "middle" };
      stationTitleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFB0C4DE" } };
      stationTitleCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };
      sheet.getRow(currentRow).height = 25;
      currentRow++;

      const stationData = data;

      // Shift Data with STEEL BLUE THEME (compact)
      if (stationData.shiftsData && stationData.shiftsData.length > 0) {
        sheet.addRow(["📌 Shift Data"]);
        sheet.mergeCells(currentRow, 1, currentRow, filteredShiftHeaders.length);
        const shiftLabelCell = sheet.getCell(currentRow, 1);
        shiftLabelCell.font = { bold: true, size: 11 };
        shiftLabelCell.alignment = { horizontal: "center", vertical: "middle" };
        shiftLabelCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
        shiftLabelCell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" }
        };
        currentRow++;

        const processedShiftData = stationData.shiftsData.map((row, idx) => ({
          "No:": idx + 1,
          "shift_loggedIn_DateTime": dayjs(row.shift_loggedIn_DateTime).format("YYYY-MM-DD hh:mm A"),
          "shift_loggedOut_DateTime": dayjs(row.shift_loggedOut_DateTime).format("YYYY-MM-DD hh:mm A"),
          // "Employees": formatEmployees(row.operator, row.astOperator, row.helpers),
          "Employees": formatEmployees(
            row.operator,
            row.AstOperator || row.astOperator,  // ✅ Try capital A first
            row.Helpers || row.helpers            // ✅ Try capital H first
          ),
          "remarks": row.remarks || ""
        }));

        const shiftTableStartRow = currentRow;
        sheet.addTable({
          name: `ShiftData_${code.replace(/[^A-Za-z0-9_]/g, "")}_${index}`,
          ref: `A${currentRow}`,
          headerRow: true,
          style: { theme: 'TableStyleMedium9', showRowStripes: true },
          columns: filteredShiftHeaders.map(col => ({ name: col })),
          rows: processedShiftData.map(obj => filteredShiftHeaders.map(col => obj[col] ?? ""))
        });

        // Add consistent steel blue borders and white background to all shift table cells
        for (let r = shiftTableStartRow; r <= shiftTableStartRow + processedShiftData.length; r++) {
          for (let c = 1; c <= filteredShiftHeaders.length; c++) {
            const cell = sheet.getCell(r, c);
            cell.border = {
              top: { style: "thin", color: { argb: "FF4682B4" } },
              left: { style: "thin", color: { argb: "FF4682B4" } },
              bottom: { style: "thin", color: { argb: "FF4682B4" } },
              right: { style: "thin", color: { argb: "FF4682B4" } }
            };
            // Add white background for data rows (skip header row)
            if (r > shiftTableStartRow) {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
            }
          }
        }

        currentRow += processedShiftData.length + 1;
      }


      // Plan Data with STEEL BLUE THEME (compact)
      if (stationData.planData && stationData.planData.length > 0) {
        sheet.addRow(["📌 Plan Vs Actual Data"]);
        sheet.mergeCells(currentRow, 1, currentRow, dynamicPlanHeaders.length);
        const planLabelCell = sheet.getCell(currentRow, 1);
        planLabelCell.font = { bold: true, size: 11 };
        planLabelCell.alignment = { horizontal: "center", vertical: "middle" };
        planLabelCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
        planLabelCell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" }
        };
        currentRow++;

        // Calculate totals for ALL numeric/quantity columns
        let totalPlannedQty = 0;
        let totalActualProductionQty = 0;
        let totalPlanSetupTimeHours = 0;
        let totalOperationalTimeHours = 0;
        let totalSetupAndOpeTimeHours = 0;
        let totalTotalSetupTimeHours = 0;
        let totalActualRunningTimeHours = 0;
        let totalTotalActualTimeHours = 0;



        const processedPlanData = stationData.planData.map((row, idx) => {
          // ✅ FIXED: Handle planned times as decimal hours (not time strings)
          const planSetupTimeHours = parseFloat(row.PlanSetupTime) || 0;
          const operationalTimeHours = parseFloat(row.OperationalTime) || 0;
          const setupAndOpeTimeHours = parseFloat(row.SetupAndOpeTime) || 0;

          // Actual times are still in HH:MM:SS format
          const totalSetupTimeSeconds = convertTimeToSeconds(row.TotalSetupTime);
          const totalSetupTimeHours = totalSetupTimeSeconds / 3600;

          // Calculate Actual Running Time and Total Actual Time
          const durationSeconds = convertTimeToSeconds(row.duration);
          const totalIdleTimeSeconds = convertTimeToSeconds(row.TotalIdleTime);
          const totalMealsTimeSeconds = convertTimeToSeconds(row.TotalMealsTime);

          const actualRunningTimeSeconds = Math.max(0, durationSeconds - totalSetupTimeSeconds - totalIdleTimeSeconds - totalMealsTimeSeconds);
          const totalActualTimeSeconds = Math.max(0, durationSeconds - totalIdleTimeSeconds - totalMealsTimeSeconds);

          // Convert to decimal hours
          const actualRunningTimeHours = actualRunningTimeSeconds / 3600;
          const totalActualTimeHours = totalActualTimeSeconds / 3600;

          // Sum totals in hours
          totalPlanSetupTimeHours += planSetupTimeHours;
          totalOperationalTimeHours += operationalTimeHours;
          totalSetupAndOpeTimeHours += setupAndOpeTimeHours;
          totalTotalSetupTimeHours += totalSetupTimeHours;
          totalActualRunningTimeHours += actualRunningTimeHours;
          totalTotalActualTimeHours += totalActualTimeHours;

          // Sum quantities
          const plannedQty = parseFloat(row.PlannedQty) || 0;

          // ✅ NEW: For QCM, use PassedQty; for others use ActualProductionQty
          const actualProdQty = isQCMGroup
            ? (parseFloat(row.PassedQty || row.ActualPassQty) || 0)
            : (parseFloat(row.ActualProductionQty) || 0);

          totalPlannedQty += plannedQty;
          totalActualProductionQty += actualProdQty;

          // ✅ Build the return object

          const returnObject = {
            "No:": idx + 1,
            "JobNumber": row.JobNumber || row.job_number || "",
            "Product": row.Product || row.product || "",
            "Customer": row.Customer || row.customer || "",
            "WOQty": row.WOQty || row.planned_wo_qty || 0,
            "OPQty": row.OPQty || row.planned_op_qty || 0,
            "PlannedQty": row.PlannedQty || row.planned_qty || 0,
            "PlannedStatus": row.PlannedStatus || row.planned_status || "",
            "MainOperation": row.MainOperation || row.main_operation || "",
            "PlanSetupTime(h)": planSetupTimeHours,
            "OperationalTime(h)": operationalTimeHours,
            "SetupAndOpeTime(h)": setupAndOpeTimeHours,
            "TotalSetupTime(h)": roundSmallValues(totalSetupTimeHours),
            "ActualRunningTime(h)": roundSmallValues(actualRunningTimeHours),
            "TotalActualTime(h)": roundSmallValues(totalActualTimeHours),
            "TimeUnit": "hrs",  // ✅ Moved here, before WOduedate
            "WOduedate": (row.WOduedate || row.planned_due_date) ? dayjs(row.WOduedate || row.planned_due_date).format("YYYY-MM-DD") : "",
            "IdleReasons": row.IdleReasons || "",
            "JobCloseRemarks": row.JobCloseremarks || row.job_close_remarks || ""
          };

          // ✅ Add the appropriate quantity field based on group
          if (isQCMGroup) {
            returnObject["PassedQty"] = actualProdQty;
          } else {
            returnObject["ActualProductionQty"] = actualProdQty;
          }

          return returnObject;
        });



        const planTableStartRow = currentRow;
        sheet.addTable({
          name: `PlanData_${code.replace(/[^A-Za-z0-9_]/g, "")}_${index}`,
          ref: `A${currentRow}`,
          headerRow: true,
          style: { theme: 'TableStyleMedium9', showRowStripes: true },
          columns: dynamicPlanHeaders.map(col => ({ name: col })),
          rows: processedPlanData.map(obj => dynamicPlanHeaders.map(col => obj[col] ?? ""))
        });


        // Add consistent steel blue borders and white background to all plan table cells
        for (let r = planTableStartRow; r <= planTableStartRow + processedPlanData.length; r++) {
          for (let c = 1; c <= dynamicPlanHeaders.length; c++) {
            const cell = sheet.getCell(r, c);
            cell.border = {
              top: { style: "thin", color: { argb: "FF4682B4" } },
              left: { style: "thin", color: { argb: "FF4682B4" } },
              bottom: { style: "thin", color: { argb: "FF4682B4" } },
              right: { style: "thin", color: { argb: "FF4682B4" } }
            };
            if (r > planTableStartRow) {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
            }
          }
        }


        currentRow += processedPlanData.length + 1;

        // Calculate overall achievement
        const overallAchievement = (() => {
          if (totalPlannedQty > 0) {
            const percentage = Math.round((totalActualProductionQty / totalPlannedQty) * 100);
            return `${percentage}%`;
          } else if (totalPlannedQty === 0) {
            if (totalActualProductionQty > 0) {
              return `Unplanned: ${totalActualProductionQty} Total`;
            } else {
              return "No Production";
            }
          } else {
            return "Invalid Plan Data";
          }
        })();

        // Add TOTAL row

        const totalRowValues = new Array(dynamicPlanHeaders.length).fill("");
        totalRowValues[0] = ""; // No:
        totalRowValues[1] = "TOTAL"; // JobNumber
        totalRowValues[2] = ""; // Product
        totalRowValues[3] = ""; // Customer
        totalRowValues[4] = ""; // WOQty
        totalRowValues[5] = ""; // OPQty
        totalRowValues[6] = totalPlannedQty; // PlannedQty
        totalRowValues[7] = ""; // PlannedStatus
        totalRowValues[8] = totalActualProductionQty; // ActualProductionQty or PassedQty
        totalRowValues[9] = ""; // MainOperation
        totalRowValues[10] = parseFloat(totalPlanSetupTimeHours.toFixed(2)); // PlanSetupTime(h)
        totalRowValues[11] = parseFloat(totalOperationalTimeHours.toFixed(2)); // OperationalTime(h)
        totalRowValues[12] = parseFloat(totalSetupAndOpeTimeHours.toFixed(2)); // SetupAndOpeTime(h)
        totalRowValues[13] = roundSmallValues(totalTotalSetupTimeHours); // TotalSetupTime(h)
        totalRowValues[14] = roundSmallValues(totalActualRunningTimeHours); // ActualRunningTime(h)
        totalRowValues[15] = roundSmallValues(totalTotalActualTimeHours); // TotalActualTime(h)
        totalRowValues[16] = ""; // TimeUnit  ✅ Moved here
        totalRowValues[17] = ""; // WOduedate
        totalRowValues[18] = ""; // IdleReasons

        // Add the total row to the sheet
        sheet.addRow(totalRowValues);

        // ✅ FIXED: Style cells with NO background fill and DARK text
        for (let c = 1; c <= dynamicPlanHeaders.length; c++) {
          const cell = sheet.getCell(currentRow, c);
          const cellValue = totalRowValues[c - 1];

          // Only style cells that have actual values
          if (cellValue !== "" && cellValue !== null && cellValue !== undefined) {
            // Dark text, bold, no background fill
            cell.font = { bold: true, color: { argb: "FF000000" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };

            const doubleUnderlineColumns = [7, 9, 13, 16];
            const hasDoubleUnderline = doubleUnderlineColumns.includes(c);

            cell.border = {
              top: { style: "thin", color: { argb: "FF4682B4" } },
              bottom: { style: hasDoubleUnderline ? "double" : "thin", color: { argb: "FF4682B4" } },
            };
          }
        }



        currentRow++;
        sheet.addRow([]);
        currentRow++;

        // ✅ ADD ACHIEVEMENT SUMMARY ROW
        const achievementText = `Overall Achievement = ${overallAchievement}`;

        // Merge columns I and J (columns 9 and 10 for PassedQty/ActualProductionQty and MainOperation)
        sheet.mergeCells(currentRow, 9, currentRow, 10);

        // Get the merged cell and SET THE VALUE + STYLE
        const achievementCell = sheet.getCell(currentRow, 9);

        // Set the value directly on the cell
        achievementCell.value = achievementText;

        achievementCell.font = {
          bold: true,
          size: 12,
          color: { argb: "FFFFFFFF" }  // White text
        };
        achievementCell.alignment = {
          horizontal: "center",
          vertical: "middle"
        };
        achievementCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF2E7D32" }  // Dark Green background
        };

        // Add borders to the achievement cell
        achievementCell.border = {
          top: { style: "thin", color: { argb: "FF2E7D32" } },
          left: { style: "thin", color: { argb: "FF2E7D32" } },
          bottom: { style: "thin", color: { argb: "FF2E7D32" } },
          right: { style: "thin", color: { argb: "FF2E7D32" } }
        };

        // Set row height for better visibility
        sheet.getRow(currentRow).height = 25;

        console.log(`✅ [MACHINE-HOUR ACHIEVEMENT] Added achievement line: ${achievementText}`);

        currentRow++;
      }

      // ✅ NON-JOB DATA SECTION - NEW SIMPLIFIED FORMAT
      if (nonJobData && nonJobData.length > 0) {
        sheet.addRow(["📌 Non-Job Related Data"]);
        sheet.mergeCells(currentRow, 1, currentRow, 2);
        const nonJobLabelCell = sheet.getCell(currentRow, 1);
        nonJobLabelCell.font = { bold: true, size: 11 };
        nonJobLabelCell.alignment = { horizontal: "center", vertical: "middle" };
        nonJobLabelCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
        nonJobLabelCell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" }
        };
        currentRow++;

        // ✅ NEW: Simple 2-column format with combined text
        const simplifiedNonJobHeaders = ["No:", "Operation Log Entry"];

        const processedNonJobData = nonJobData.map((row, idx) => {
          const startTime = dayjs(row.start_datetime).format("h.mmA");
          const endTime = dayjs(row.end_datetime).format("h.mmA");
          const durationMinutes = Math.round((row.duration_hours || 0) * 60);

          const logEntry = `*${row.operation_description || ""} = ${startTime} - ${endTime} = ${durationMinutes}min`;

          return {
            "No:": idx + 1,
            "Operation Log Entry": logEntry
          };
        });

        const nonJobTableStartRow = currentRow;
        sheet.addTable({
          name: `NonJobData_${code.replace(/[^A-Za-z0-9_]/g, "")}_${index}`,
          ref: `A${currentRow}`,
          headerRow: true,
          style: { theme: 'TableStyleMedium9', showRowStripes: true },
          columns: simplifiedNonJobHeaders.map(col => ({ name: col })),
          rows: processedNonJobData.map(obj => simplifiedNonJobHeaders.map(col => obj[col] ?? ""))
        });

        // Add consistent steel blue borders and white background
        for (let r = nonJobTableStartRow; r <= nonJobTableStartRow + processedNonJobData.length; r++) {
          for (let c = 1; c <= simplifiedNonJobHeaders.length; c++) {
            const cell = sheet.getCell(r, c);
            cell.border = {
              top: { style: "thin", color: { argb: "FF4682B4" } },
              left: { style: "thin", color: { argb: "FF4682B4" } },
              bottom: { style: "thin", color: { argb: "FF4682B4" } },
              right: { style: "thin", color: { argb: "FF4682B4" } }
            };
            // Add white background for data rows (skip header row)
            if (r > nonJobTableStartRow) {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
            }
            // ✅ Make the "Operation Log Entry" column wider
            if (c === 2) {
              sheet.getColumn(2).width = 60;
            }
          }
        }

        currentRow += processedNonJobData.length + 1;
        sheet.addRow([]); // Add empty row for proper spacing
        currentRow++;
      }

      // Add to full opelog sheet with STEEL BLUE THEME (compact)
      if (fullOpelog && fullOpelog.length > 0) {
        fullOpelogSheet.addRow([`${code} - Full Operation Log`]);
        const opelogSectionHeader = fullOpelogSheet.lastRow;
        opelogSectionHeader.font = { bold: true, size: 11 };
        opelogSectionHeader.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFB0C4DE" } };

        fullOpelogSheet.addRow(["operation_code", "operation_description", "start_datetime", "end_datetime", "remarks", "duration_hours"]);
        const headerRow = fullOpelogSheet.lastRow;
        headerRow.font = { bold: true, size: 10 };
        headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4682B4" } };

        const fullOpelogStartRow = fullOpelogSheet.lastRow.number;
        fullOpelog.forEach(item => {
          fullOpelogSheet.addRow([
            item.operation_code,
            item.operation_description,
            dayjs(item.start_datetime).format("YYYY-MM-DD hh:mm A"),
            dayjs(item.end_datetime).format("YYYY-MM-DD hh:mm A"),
            item.remarks || "",
            item.duration_hours
          ]);
        });

        // Add steel blue borders and white background to full opelog
        for (let r = fullOpelogStartRow; r <= fullOpelogSheet.lastRow.number; r++) {
          for (let c = 1; c <= 6; c++) {
            const cell = fullOpelogSheet.getCell(r, c);
            cell.border = {
              top: { style: "thin", color: { argb: "FF4682B4" } },
              left: { style: "thin", color: { argb: "FF4682B4" } },
              bottom: { style: "thin", color: { argb: "FF4682B4" } },
              right: { style: "thin", color: { argb: "FF4682B4" } }
            };
            // Add white background for data rows (skip header row)
            if (r > fullOpelogStartRow) {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
            }
          }
        }

      }

      // Clean separation between workstations
      if (index < groupData.length - 1) {
        sheet.addRow([]);
        currentRow++;
        sheet.addRow([]);
        currentRow++;
      }


    });

    // Optimize column widths (more compact)
    sheet.columns.forEach(col => {
      if (!col) return;
      let maxLength = 0;
      col.eachCell({ includeEmpty: false }, cell => {
        const text = cell.value ? cell.value.toString() : "";
        if (text.length > maxLength) maxLength = text.length;
      });
      col.width = Math.min(25, Math.max(8, maxLength + 1));
    });

    fullOpelogSheet.columns.forEach(col => {
      if (!col) return;
      let maxLength = 0;
      col.eachCell({ includeEmpty: false }, cell => {
        const text = cell.value ? cell.value.toString() : "";
        if (text.length > maxLength) maxLength = text.length;
      });
      col.width = Math.min(25, Math.max(8, maxLength + 1));
    });
  };

  // Man-Hour sheet generation function with CONSISTENT COOL COLOR THEME + LOGGING
  const generateManHourGroupedSheet = (workbook, groupCode, groupData, selectedDate) => {


    const sheet = workbook.addWorksheet(`${groupCode}_ManHour_PlanVsActual`);

    let currentRow = 1;

    // Group title - STEEL BLUE THEME for Man-Hour
    const groupTitleText = `${groupCode} Group - Man-Hour Plan Vs Actual - ${selectedDate.format("YYYY-MM-DD")}`;
    sheet.addRow([groupTitleText]);
    sheet.mergeCells(currentRow, 1, currentRow, 15);
    const groupTitleCell = sheet.getCell(currentRow, 1);
    groupTitleCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
    groupTitleCell.alignment = { horizontal: "center", vertical: "middle" };
    groupTitleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4682B4" } };
    sheet.getRow(currentRow).height = 35;
    currentRow++;
    sheet.addRow([]);
    currentRow++;

    // Process each workstation
    groupData.forEach(({ code, data }, index) => {

      // Workstation title - LIGHT STEEL BLUE
      sheet.addRow([`📍 ${code}`]);
      sheet.mergeCells(currentRow, 1, currentRow, 17);
      const stationTitleCell = sheet.getCell(currentRow, 1);
      stationTitleCell.font = { bold: true, size: 14 };
      stationTitleCell.alignment = { horizontal: "left", vertical: "middle" };
      stationTitleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFB0C4DE" } };
      stationTitleCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };
      sheet.getRow(currentRow).height = 25;
      currentRow++;

      const stationData = data;


      let combinedEmployeeData = [];

      if (stationData.employeeActivitiesData && stationData.employeeActivitiesData.length > 0) {
        // ✅ NEW: Process the optimized backend JOIN data
        combinedEmployeeData = processEmployeeActivitiesData(stationData.employeeActivitiesData);
      } else {
        // ✅ FALLBACK: Use legacy combination method if new data structure not available

        combinedEmployeeData = combineEmployeeActivities(stationData.shiftsData, stationData.opelogData);
      }

      if (combinedEmployeeData && combinedEmployeeData.length > 0) {


        sheet.addRow(["📌 Employee Activities & Shifts"]);
        sheet.mergeCells(currentRow, 1, currentRow, combinedEmployeeActivitiesHeaders.length);
        const activitiesLabelCell = sheet.getCell(currentRow, 1);
        activitiesLabelCell.font = { bold: true };
        activitiesLabelCell.alignment = { horizontal: "center", vertical: "middle" };
        activitiesLabelCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
        activitiesLabelCell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" }
        };
        currentRow++;

        const activitiesTableStartRow = currentRow;
        sheet.addTable({
          name: `EmployeeActivities_${code.replace(/[^A-Za-z0-9_]/g, "")}_${index}`,
          ref: `A${currentRow}`,
          headerRow: true,
          style: { theme: 'TableStyleMedium9', showRowStripes: true },
          columns: combinedEmployeeActivitiesHeaders.map(col => ({ name: col })),
          rows: combinedEmployeeData.map(obj => combinedEmployeeActivitiesHeaders.map(col => obj[col] ?? ""))
        });

        // Add consistent steel blue borders and white background to all activity table cells
        for (let r = activitiesTableStartRow; r <= activitiesTableStartRow + combinedEmployeeData.length; r++) {
          for (let c = 1; c <= combinedEmployeeActivitiesHeaders.length; c++) {
            const cell = sheet.getCell(r, c);
            cell.border = {
              top: { style: "thin", color: { argb: "FF4682B4" } },
              left: { style: "thin", color: { argb: "FF4682B4" } },
              bottom: { style: "thin", color: { argb: "FF4682B4" } },
              right: { style: "thin", color: { argb: "FF4682B4" } }
            };
            // Add white background for data rows (skip header row)
            if (r > activitiesTableStartRow) {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
            }
            // ✅ ENHANCED: Better formatting for the non_job_activities column (column 6)
            if (c === 6) {
              cell.alignment = {
                horizontal: "left",
                vertical: "top",
                wrapText: true,
                indent: 1
              };
              // ✅ Set a minimum row height for activities with line breaks
              if (r > activitiesTableStartRow && cell.value && cell.value.toString().includes('\n')) {
                const lineCount = (cell.value.toString().match(/\n/g) || []).length + 1;
                const minHeight = Math.max(20, lineCount * 18);
                sheet.getRow(r).height = minHeight;
              }
              // ✅ Set a wider column for activities
              sheet.getColumn(6).width = 50;
            }
          }
        }

        currentRow += combinedEmployeeData.length + 1;
        sheet.addRow([]);
        currentRow++;
      }



      // Plan Data for Man-Hour - CONSISTENT STEEL BLUE THEME
      if (stationData.planData && stationData.planData.length > 0) {
        sheet.addRow(["📌 Man-Hour Plan Vs Actual Data"]);
        sheet.mergeCells(currentRow, 1, currentRow, manHourPlanHeaders.length);
        const planLabelCell = sheet.getCell(currentRow, 1);
        planLabelCell.font = { bold: true };
        planLabelCell.alignment = { horizontal: "center", vertical: "middle" };
        planLabelCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
        planLabelCell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" }
        };
        currentRow++;

        // Calculate totals for Man-Hour metrics
        let totalPlannedQty = 0;
        let totalActualProductionQty = 0;
        let totalPlannedSetupTime = 0;
        let totalPlannedOperationalTime = 0;
        let totalPlannedSetupOperationalTime = 0;
        let totalActualSetupTime = 0;        // ✅ NEW
        let totalActualRunningTime = 0;      // ✅ NEW
        let totalTotalProdTime = 0;

        const processedPlanData = stationData.planData.map((row, idx) => {
          // Use snake_case for man hour table
          const plannedQty = parseFloat(row.planned_qty || 0);
          const actualProdQty = parseFloat(row.ActualProductionQty || row.actual_prod_qty || 0);
          const plannedSetupTime = parseFloat(row.planned_setup_time || 0);
          const plannedOperationalTime = parseFloat(row.planned_operational_time || 0);
          const plannedSetupOperationalTime = plannedSetupTime + plannedOperationalTime;
          const totalProdTimeDecimal = convertTimeToDecimalHours(row.TotalProdTime || row.total_prod_time || 0);


          const actualSetupTime = 0;  // Always 0 for man-hour
          const actualRunningTime = totalProdTimeDecimal;

          totalPlannedQty += plannedQty;
          totalActualProductionQty += actualProdQty;
          totalPlannedSetupTime += plannedSetupTime;
          totalPlannedOperationalTime += plannedOperationalTime;
          totalPlannedSetupOperationalTime += plannedSetupOperationalTime;
          totalActualSetupTime += actualSetupTime;           // ✅ NEW
          totalActualRunningTime += actualRunningTime;       // ✅ NEW
          totalTotalProdTime += totalProdTimeDecimal;


          const processedRecord = {
            "No:": idx + 1,
            "JobNumber": row.JobNumber || row.job_number || "",
            "Product": row.Product || row.product || "",
            "Customer": row.Customer || row.customer || "",
            "MainOperation": row.MainOperation || row.main_operation || "",
            "PlannedQty": row.PlannedQty || row.planned_qty || 0,
            "PlannedStatus": row.PlannedStatus || row.planned_status || "",
            "ActualProductionQty": row.ActualProductionQty || row.actual_prod_qty || 0,
            "PlannedSetupTime": formatDecimalHours(row.planned_setup_time || 0),
            "PlannedOperationalTime": formatDecimalHours(row.planned_operational_time || 0),
            "PlannedSetup+OperationalTime": formatDecimalHours(plannedSetupOperationalTime),
            "ActualSetupTime": formatDecimalHours(actualSetupTime),           // ✅ NEW (always 0)
            "ActualRunningTime": formatDecimalHours(actualRunningTime),
            "TotalProdTime": formatDecimalHours(convertTimeToDecimalHours(row.TotalProdTime || row.total_prod_time || 0)),
            "TimeUnit": "Man hrs",
            "EmployeeList": row.EmployeeList || row.employee_list || ""
          };
          return processedRecord;
        });

        const planTableStartRow = currentRow;
        sheet.addTable({
          name: `ManHourPlanData_${code.replace(/[^A-Za-z0-9_]/g, "")}_${index}`,
          ref: `A${currentRow}`,
          headerRow: true,
          style: { theme: 'TableStyleMedium9', showRowStripes: true },
          columns: manHourPlanHeaders.map(col => ({ name: col })),
          rows: processedPlanData.map(obj => manHourPlanHeaders.map(col => obj[col] ?? ""))
        });

        // ✅ FIXED: Add consistent steel blue borders and white background to all plan table cells
        for (let r = planTableStartRow; r <= planTableStartRow + processedPlanData.length; r++) {
          for (let c = 1; c <= manHourPlanHeaders.length; c++) {
            const cell = sheet.getCell(r, c);
            cell.border = {
              top: { style: "thin", color: { argb: "FF4682B4" } },
              left: { style: "thin", color: { argb: "FF4682B4" } },
              bottom: { style: "thin", color: { argb: "FF4682B4" } },
              right: { style: "thin", color: { argb: "FF4682B4" } }
            };
            // Add white background for data rows (skip header row)
            if (r > planTableStartRow) {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
            }
          }
        }

        currentRow += processedPlanData.length + 1;

        // ✅ Calculate overall achievement
        const overallAchievement = (() => {
          if (totalPlannedQty > 0) {
            const percentage = Math.round((totalActualProductionQty / totalPlannedQty) * 100);
            return `${percentage}%`;
          } else if (totalPlannedQty === 0) {
            if (totalActualProductionQty > 0) {
              return `Unplanned: ${totalActualProductionQty} Total`;
            } else {
              return "No Production";
            }
          } else {
            return "Invalid Plan Data";
          }
        })();



        const totalRowValues = new Array(manHourPlanHeaders.length).fill("");
        totalRowValues[0] = ""; // No:
        totalRowValues[1] = "TOTAL"; // JobNumber
        totalRowValues[2] = ""; // Product
        totalRowValues[3] = ""; // Customer
        totalRowValues[4] = ""; // MainOperation
        totalRowValues[5] = totalPlannedQty; // PlannedQty
        totalRowValues[6] = ""; // PlannedStatus
        totalRowValues[7] = totalActualProductionQty; // ActualProductionQty
        totalRowValues[8] = formatDecimalHours(totalPlannedSetupOperationalTime); // PlannedSetup+OperationalTime (moved up from index 9)
        totalRowValues[9] = formatDecimalHours(totalPlannedSetupTime); // PlannedSetupTime (moved up from index 10)
        totalRowValues[10] = formatDecimalHours(totalPlannedOperationalTime);
        totalRowValues[11] = formatDecimalHours(totalActualSetupTime); // ✅ ActualSetupTime (always 0)
        totalRowValues[12] = formatDecimalHours(totalActualRunningTime);
        totalRowValues[11] = formatDecimalHours(totalTotalProdTime); // TotalProdTime (moved up from index 12)
        totalRowValues[12] = ""; // EmployeeList (moved up from index 13)

        const totalRow = sheet.addRow(totalRowValues);
        totalRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
        totalRow.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF4682B4" }
        };
        totalRow.alignment = { horizontal: "center", vertical: "middle" };

        // ✅ FIXED: Add steel blue borders ONLY to cells with values in totals row
        for (let c = 1; c <= manHourPlanHeaders.length; c++) {
          const cell = sheet.getCell(currentRow, c);
          const cellValue = totalRowValues[c - 1];

          if (cellValue !== "" && cellValue !== null && cellValue !== undefined) {
            cell.border = {
              top: { style: "thin", color: { argb: "FF4682B4" } },
              left: { style: "thin", color: { argb: "FF4682B4" } },
              bottom: { style: "double", color: { argb: "FF4682B4" } },
              right: { style: "thin", color: { argb: "FF4682B4" } }
            };
          }
        }

        currentRow++;
        sheet.addRow([]);
        currentRow++;

        // ✅ ADD ACHIEVEMENT SUMMARY ROW
        const achievementText = `Overall Achievement = ${overallAchievement}`;

        // Merge columns H and I (columns 8 and 9 in 1-indexed)
        sheet.mergeCells(currentRow, 7, currentRow, 8);

        const achievementCell = sheet.getCell(currentRow, 7);
        achievementCell.value = achievementText;

        achievementCell.font = {
          bold: true,
          size: 12,
          color: { argb: "FFFFFFFF" }
        };
        achievementCell.alignment = {
          horizontal: "center",
          vertical: "middle"
        };
        achievementCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF2E7D32" }
        };
        achievementCell.border = {
          top: { style: "thin", color: { argb: "FF2E7D32" } },
          left: { style: "thin", color: { argb: "FF2E7D32" } },
          bottom: { style: "thin", color: { argb: "FF2E7D32" } },
          right: { style: "thin", color: { argb: "FF2E7D32" } }
        };

        sheet.getRow(currentRow).height = 25;
        console.log(`✅ [MAN-HOUR ACHIEVEMENT] Added achievement line: ${achievementText}`);
        currentRow++;
      }


      if (index < groupData.length - 1) {
        sheet.addRow([]);
        currentRow++;
        sheet.addRow([]);
        currentRow++;
      }







    });

    // Adjust columns for Man-Hour
    sheet.columns.forEach(col => {
      if (!col) return;
      let maxLength = 0;
      col.eachCell({ includeEmpty: false }, cell => {
        const text = cell.value ? cell.value.toString() : "";
        if (text.length > maxLength) maxLength = text.length;
      });
      col.width = Math.min(30, Math.max(10, maxLength + 2));
    });
  };

  const fetchFilteredData = async () => {

    if (!selectedDate) {
      setAlert({ open: true, severity: "warning", message: "Please select a date first." });
      return;
    }

    try {
      setIsLoadingMachineHour(true);
      setIsLoadingManHour(true);
      setHasFetchedData(true);

      // Fetch both machine-hour and man-hour data in parallel
      const [machineHourResponse, manHourResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/planvsactualgroupfilteration_machinehour`, {
          params: { selectedDate: selectedDate.format("YYYY-MM-DD") }
        }),
        axios.get(`${API_BASE_URL}/planvsactualgroupfilteration_manhour`, {
          params: { selectedDate: selectedDate.format("YYYY-MM-DD") }
        })
      ]);


      setMachineHourCount(machineHourResponse.data.length);
      setManHourCount(manHourResponse.data.length);

      // Combine and process both datasets
      const allData = [...machineHourResponse.data, ...manHourResponse.data];

      if (allData.length === 0) {
        setAlert({ open: true, severity: "info", message: "No machine codes available for the selected date." });
        setData({});
        setWorkstationTypes({});
        setHoursType('');
        setShowWorkstations(false);
        setShowDownloadButton(false);
        return;
      }

      // Process workstation types
      const types = {};
      allData.forEach(item => {
        types[item.code] = item.HoursType;
      });
      setWorkstationTypes(types);

      // Determine overall hours type
      const machineHourCount = Object.values(types).filter(type => type === 'Machine-Hour' || type === 'Machine').length;
      const manHourCount = Object.values(types).filter(type => type === 'Man-Hour' || type === 'Man').length;

      let overallHoursType = '';
      if (machineHourCount > 0 && manHourCount > 0) {
        overallHoursType = 'Mixed';
      } else if (machineHourCount > 0) {
        overallHoursType = 'Machine-Hour';
      } else if (manHourCount > 0) {
        overallHoursType = 'Man-Hour';
      }
      setHoursType(overallHoursType);

      // Group data by MachineGroupCode
      const groupedData = allData.reduce((acc, item) => {
        if (!acc[item.MachineGroupCode]) {
          acc[item.MachineGroupCode] = [];
        }
        acc[item.MachineGroupCode].push(item.code);
        return acc;
      }, {});

      setData(groupedData);
      setShowWorkstations(true);
      setShowDownloadButton(true);

    } catch (error) {
      console.error("Error fetching data: ", error);
      setAlert({ open: true, severity: "error", message: "An error occurred while fetching data." });
      setShowWorkstations(false);
      setShowDownloadButton(false);
    } finally {
      setIsLoadingMachineHour(false);
      setIsLoadingManHour(false);
    }
  };

  const handleGroupCheckboxChange = (group) => {
    const newSelectedItems = { ...selectedItems };
    const allChecked = Object.values(selectedItems[group] || {}).every(Boolean);

    newSelectedItems[group] = {};
    data[group].forEach(machine => {
      newSelectedItems[group][machine] = !allChecked;
    });

    setSelectedItems(newSelectedItems);
  };

  const handleMachineCheckboxChange = (group, machine) => {
    const newSelectedItems = { ...selectedItems };
    if (!newSelectedItems[group]) {
      newSelectedItems[group] = {};
    }
    newSelectedItems[group][machine] = !newSelectedItems[group][machine];
    setSelectedItems(newSelectedItems);
  };

  // ✅ UPDATED: Enhanced handleDownload function with comprehensive logging for root cause analysis
  const handleDownload = async () => {
    try {
      const workbook = new ExcelJS.Workbook();

      // Get selected workstations by group
      const selectedByGroup = {};
      Object.entries(selectedItems).forEach(([groupCode, machines]) => {
        const selectedMachines = Object.entries(machines)
          .filter(([_, isSelected]) => isSelected)
          .map(([machine]) => machine);

        if (selectedMachines.length > 0) {
          selectedByGroup[groupCode] = selectedMachines;
        }
      });

      const allSelectedWorkstations = Object.values(selectedByGroup).flat();

      if (allSelectedWorkstations.length === 0) {
        setAlert({ open: true, severity: "warning", message: "Please select at least one workstation." });
        return;
      }



      if (hoursType === 'Mixed') {
        // Handle mixed environment
        const workstationsWithTypes = allSelectedWorkstations.map(code => ({
          code,
          hoursType: workstationTypes[code]
        }));



        const response = await axios.post(`${API_BASE_URL}/planvsactualmultidownload_unified`, {
          selectedDate: selectedDate.format("YYYY-MM-DD"),
          workstations: workstationsWithTypes
        });




        const machineHourCodes = workstationsWithTypes
          .filter(w => w.hoursType === 'Machine-Hour' || w.hoursType === 'Machine')
          .map(w => w.code);

        let fullShiftOperationLogResponse = { data: {} };
        // ✅ NEW: Fetch non-job related data for main sheet
        let nonJobDataResponse = { data: {} };

        if (machineHourCodes.length > 0) {


          [fullShiftOperationLogResponse, nonJobDataResponse] = await Promise.all([
            axios.post(`${API_BASE_URL}/ViewOperationlogForPlanVsActual`, {
              startTime: dayjs(selectedDate).hour(7).minute(30).second(0).format("YYYY-MM-DD HH:mm:ss"),
              endTime: dayjs(selectedDate).add(1, "day").hour(7).minute(30).second(0).format("YYYY-MM-DD HH:mm:ss"),
              machineCodes: machineHourCodes
            }),
            axios.post(`${API_BASE_URL}/ViewPlanVsActualNonJobData`, {
              machineCodes: machineHourCodes,
              selectedDate: selectedDate.format("YYYY-MM-DD")
            })
          ]);


        }

        // Separate by type and group
        const machineHourByGroup = {};
        const manHourByGroup = {};

        Object.entries(selectedByGroup).forEach(([groupCode, workstations]) => {
          const machineHourInGroup = workstations.filter(code =>
            workstationTypes[code] === 'Machine-Hour' || workstationTypes[code] === 'Machine'
          );
          const manHourInGroup = workstations.filter(code =>
            workstationTypes[code] === 'Man-Hour' || workstationTypes[code] === 'Man'
          );

          if (machineHourInGroup.length > 0) {
            machineHourByGroup[groupCode] = machineHourInGroup;
          }
          if (manHourInGroup.length > 0) {
            manHourByGroup[groupCode] = manHourInGroup;
          }
        });


        // Generate Machine-Hour grouped sheets
        Object.entries(machineHourByGroup).forEach(([groupCode, workstations]) => {
          const groupData = workstations.map(code => {
            const machineData = {
              code,
              data: response.data[code] || {},
              fullOpelog: fullShiftOperationLogResponse.data[code] || [],
              // ✅ NEW: Add non-job data
              nonJobData: nonJobDataResponse.data[code] || []
            };



            return machineData;
          });

          generateMachineHourGroupedSheet(workbook, groupCode, groupData, selectedDate);
        });

        // Generate Man-Hour grouped sheets
        Object.entries(manHourByGroup).forEach(([groupCode, workstations]) => {
          const groupData = workstations.map(code => {
            const machineData = {
              code,
              data: response.data[code] || {}
            };

            return machineData;
          });

          generateManHourGroupedSheet(workbook, groupCode, groupData, selectedDate);
        });

      } else if (hoursType === 'Man-Hour') {


        // ✅ NEW: Log the exact API request being sent
        const requestPayload = {
          selectedDate: selectedDate.format("YYYY-MM-DD"),
          workstations: allSelectedWorkstations
        };

        const response = await axios.post(`${API_BASE_URL}/planvsactualmultidownload_manhour`, requestPayload);

        Object.entries(selectedByGroup).forEach(([groupCode, workstations]) => {
          const groupData = workstations.map(code => ({
            code,
            data: response.data[code] || {}
          }));
          generateManHourGroupedSheet(workbook, groupCode, groupData, selectedDate);
        });

      } else {
        // Machine-Hour: Grouped sheets

        const [response, fullShiftOperationLogResponse, nonJobDataResponse] = await Promise.all([
          axios.post(`${API_BASE_URL}/planvsactualmultidownload`, {
            selectedDate: selectedDate.format("YYYY-MM-DD"),
            machineCodes: allSelectedWorkstations
          }),
          axios.post(`${API_BASE_URL}/ViewOperationlogForPlanVsActual`, {
            startTime: dayjs(selectedDate).hour(7).minute(30).second(0).format("YYYY-MM-DD HH:mm:ss"),
            endTime: dayjs(selectedDate).add(1, "day").hour(7).minute(30).second(0).format("YYYY-MM-DD HH:mm:ss"),
            machineCodes: allSelectedWorkstations
          }),
          // ✅ NEW: Fetch non-job related data for main sheet
          axios.post(`${API_BASE_URL}/ViewPlanVsActualNonJobData`, {
            machineCodes: allSelectedWorkstations,
            selectedDate: selectedDate.format("YYYY-MM-DD")
          })
        ]);


        // Generate one sheet per group
        Object.entries(selectedByGroup).forEach(([groupCode, workstations]) => {
          const groupData = workstations.map(code => ({
            code,
            data: response.data[code] || {},
            fullOpelog: fullShiftOperationLogResponse.data[code] || [],
            // ✅ NEW: Add non-job data
            nonJobData: nonJobDataResponse.data[code] || []
          }));
          generateMachineHourGroupedSheet(workbook, groupCode, groupData, selectedDate);
        });
      }

      const now = dayjs();
      const timePart = now.format("hhmmssA");
      const datePart = selectedDate.format("DDMMMMYYYY");
      const fileName = `PlanVsActual_${datePart}_${timePart}.xlsx`;


      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, fileName);


      setAlert({
        open: true,
        severity: "success",
        message: `Excel file "${fileName}" downloaded successfully!`,
      });

    } catch (error) {
      console.error("❌ [DOWNLOAD ERROR] Error during download:", error);
      console.error("❌ [ERROR DETAILS]:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setAlert({
        open: true,
        severity: "error",
        message: "An error occurred during download. Please try again.",
      });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg">
        <Fade in={true} timeout={1000}>
          <Card sx={{ p: 3, mb: 3, borderRadius: "12px", background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)" }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: "#2c3e50", textAlign: "center" }}>
              Plan Vs Actual Reports - View & Download
            </Typography>
            <Typography variant="body2" sx={{ color: "#7f8c8d", textAlign: "center", mb: 3 }}>
              Select a date to view available workstations and download comprehensive plan vs actual reports
            </Typography>

            <Grid container spacing={3} alignItems="center" justifyContent="center">
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  label="Select Report Date"
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  sx={{ width: "100%" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  variant="contained"
                  onClick={fetchFilteredData}
                  fullWidth
                  sx={{
                    height: "56px",
                    background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                    boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                    "&:hover": {
                      background: "linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 10px 2px rgba(33, 203, 243, .3)",
                    },
                    transition: "all 0.3s ease"
                  }}
                >
                  Show Workstations
                </Button>
              </Grid>
            </Grid>

            {hasFetchedData && (
              <Fade in={hasFetchedData} timeout={1000}>
                <Box sx={{
                  mt: 2,
                  p: 1.5,
                  backgroundColor: '#f8f9fa',
                  borderRadius: 2,
                  border: '1px solid #e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#495057', fontSize: '0.9rem' }}>
                    📊 Data Found:
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Machine-Hour Compact Indicator */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isLoadingMachineHour ? (
                        <>
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              border: '2px solid #2196f3',
                              borderTopColor: 'transparent',
                              borderRadius: '50%',
                              animation: 'spin 0.8s linear infinite',
                              '@keyframes spin': {
                                '0%': { transform: 'rotate(0deg)' },
                                '100%': { transform: 'rotate(360deg)' }
                              }
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#2196f3' }}>
                            Machine-Hour...
                          </Typography>
                        </>
                      ) : (
                        <Fade in={!isLoadingMachineHour} timeout={500}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: machineHourCount > 0 ? '#e8f5e8' : '#f5f5f5',
                            borderRadius: 1,
                            px: 1.5,
                            py: 0.5,
                            border: machineHourCount > 0 ? '1px solid #4caf50' : '1px solid #ddd'
                          }}>
                            <Typography variant="body2" sx={{
                              mr: 0.5,
                              fontSize: '0.9rem',
                              color: machineHourCount > 0 ? '#2e7d32' : '#666'
                            }}>
                              🏭
                            </Typography>
                            <Typography variant="body2" sx={{
                              fontSize: '0.85rem',
                              color: machineHourCount > 0 ? '#2e7d32' : '#666',
                              fontWeight: 500
                            }}>
                              {machineHourCount} Machine-Hour
                            </Typography>
                          </Box>
                        </Fade>
                      )}
                    </Box>

                    {/* Man-Hour Compact Indicator */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isLoadingManHour ? (
                        <>
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              border: '2px solid #9c27b0',
                              borderTopColor: 'transparent',
                              borderRadius: '50%',
                              animation: 'spin 0.8s linear infinite',
                              '@keyframes spin': {
                                '0%': { transform: 'rotate(0deg)' },
                                '100%': { transform: 'rotate(360deg)' }
                              }
                            }}
                          />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#9c27b0' }}>
                            Man-Hour...
                          </Typography>
                        </>
                      ) : (
                        <Fade in={!isLoadingManHour} timeout={500}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: manHourCount > 0 ? '#f3e5f5' : '#f5f5f5',
                            borderRadius: 1,
                            px: 1.5,
                            py: 0.5,
                            border: manHourCount > 0 ? '1px solid #9c27b0' : '1px solid #ddd'
                          }}>
                            <Typography variant="body2" sx={{
                              mr: 0.5,
                              fontSize: '0.9rem',
                              color: manHourCount > 0 ? '#7b1fa2' : '#666'
                            }}>
                              👥
                            </Typography>
                            <Typography variant="body2" sx={{
                              fontSize: '0.85rem',
                              color: manHourCount > 0 ? '#7b1fa2' : '#666',
                              fontWeight: 500
                            }}>
                              {manHourCount} Man-Hour
                            </Typography>
                          </Box>
                        </Fade>
                      )}
                    </Box>

                    {/* Total Summary */}
                    {!isLoadingMachineHour && !isLoadingManHour && (
                      <Fade in={!isLoadingMachineHour && !isLoadingManHour} timeout={700}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: '#e3f2fd',
                          borderRadius: 1,
                          px: 1.5,
                          py: 0.5,
                          border: '1px solid #2196f3'
                        }}>
                          <Typography variant="body2" sx={{
                            fontSize: '0.85rem',
                            color: '#1976d2',
                            fontWeight: 600
                          }}>
                            📈 Total: {machineHourCount + manHourCount} Workstations
                          </Typography>
                        </Box>
                      </Fade>
                    )}
                  </Box>
                </Box>
              </Fade>
            )}
          </Card>
        </Fade>




        {showWorkstations && (
          <Slide direction="up" in={showWorkstations} mountOnEnter unmountOnExit>
            <Card sx={{ mt: 2, borderRadius: "8px", overflow: "hidden" }}>
              {/* Header section */}
              <Box sx={{
                p: 1.5,
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#495057', fontSize: '0.9rem' }}>
                  📍 Available Workstations
                </Typography>
                <Typography variant="caption" sx={{ color: '#6c757d', fontSize: '0.75rem' }}>
                  {Object.keys(data).length} groups • {Object.values(data).flat().length} machines
                </Typography>
              </Box>

              {/* ✅ TABLE FORMAT: Each group in one row */}
              <Box sx={{ p: 0 }}> {/* Remove padding for table format */}
                {Object.keys(data).length > 0 ? (
                  <Box>
                    {/* Table Header Row */}
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: '200px 1fr 80px', // Group name | Machines | Count
                      backgroundColor: '#f8f9fa',
                      borderBottom: '1px solid #e0e0e0',
                      py: 1,
                      px: 1.5,
                      alignItems: 'center'
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#495057' }}>
                        Group Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#495057' }}>
                        Workstations
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#495057', textAlign: 'center' }}>
                        Count
                      </Typography>
                    </Box>

                    {/* Table Body Rows */}
                    {Object.entries(data).map(([group, machines], index) => (
                      <Box key={group} sx={{
                        display: 'grid',
                        gridTemplateColumns: '200px 1fr 80px', // Same as header
                        borderBottom: index < Object.entries(data).length - 1 ? '1px solid #f0f0f0' : 'none',
                        py: 1,
                        px: 1.5,
                        alignItems: 'center',
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa', // Alternating row colors
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        },
                        minHeight: '48px' // Consistent row height
                      }}>
                        {/* Group Name Column (Left) */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Checkbox
                            size="small"
                            checked={selectedItems[group] && Object.values(selectedItems[group]).every(Boolean)}
                            indeterminate={
                              selectedItems[group] &&
                              Object.values(selectedItems[group]).some(Boolean) &&
                              !Object.values(selectedItems[group]).every(Boolean)
                            }
                            onChange={() => handleGroupCheckboxChange(group)}
                            sx={{
                              p: 0.25,
                              '& .MuiSvgIcon-root': { fontSize: '1rem' }
                            }}
                          />
                          <Typography variant="body2" sx={{
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            color: '#343a40',
                            lineHeight: 1.2
                          }}>
                            {group}
                          </Typography>
                        </Box>

                        {/* Machines Column (Center/Right) */}
                        <Box sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          alignItems: 'center',
                          justifyContent: 'flex-start', // Align machines to left within their column
                          px: 1 // Small padding from group name
                        }}>
                          {machines.map((machine) => {
                            const type = workstationTypes[machine];
                            const isSelected = selectedItems[group]?.[machine];
                            const label = hoursType === 'Mixed'
                              ? `${machine} [${type === 'Man-Hour' || type === 'Man' ? 'MH' : 'MCH'}]`
                              : machine;
                            return (
                              <Chip
                                key={machine}
                                label={label}
                                size="small"
                                onClick={() => handleMachineCheckboxChange(group, machine)}
                                color={
                                  isSelected
                                    ? (type === 'Man-Hour' || type === 'Man' ? 'secondary' : 'primary')
                                    : 'default'
                                }
                                variant={isSelected ? "filled" : "outlined"}
                                sx={{
                                  cursor: "pointer",
                                  fontSize: '0.7rem',
                                  height: '22px',
                                  '& .MuiChip-label': {
                                    px: 0.5,
                                    py: 0
                                  },
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: 1
                                  }
                                }}
                              />
                            );
                          })}
                        </Box>

                        {/* Count Column (Right) */}
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{
                            color: '#6c757d',
                            fontSize: '0.7rem',
                            backgroundColor: '#e9ecef',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 0.5,
                            fontWeight: 500
                          }}>
                            {machines.length}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  hasFetchedData && (
                    <Fade in={hasFetchedData} timeout={1000}>
                      <Box sx={{
                        textAlign: 'center',
                        py: 3,
                        color: '#6c757d'
                      }}>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                          📅 No workstations available for{" "}
                          <strong>{selectedDate ? selectedDate.format("MMMM D, YYYY") : ""}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', mt: 0.5, display: 'block' }}>
                          Try selecting a different date or check if data has been generated
                        </Typography>
                      </Box>
                    </Fade>
                  )
                )}
              </Box>
            </Card>
          </Slide>
        )}

        {/* ✅ ALTERNATIVE: More compact table version */}


        {Object.keys(selectedItems).length > 0 && (
          <Collapse in={Object.keys(selectedItems).length > 0} timeout={500}>
            <Paper
              elevation={3}
              sx={{
                p: 2, borderRadius: "12px", mt: 2, maxWidth: "100%",
                backgroundColor: "#f9f9f9", border: "1px solid #ddd"
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Selected Items
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: 1 }}>
                {Object.entries(selectedItems).flatMap(([group, machines]) =>
                  Object.entries(machines)
                    .filter(([_, isChecked]) => isChecked)
                    .map(([machine]) => {
                      const type = workstationTypes[machine];
                      const label = hoursType === 'Mixed'
                        ? `${machine}`
                        : machine;
                      return (
                        <Chip
                          key={machine}
                          label={label}
                          onDelete={() => handleMachineCheckboxChange(group, machine)}
                          color={type === 'Man-Hour' ? 'secondary' : 'primary'}
                          variant="outlined"
                          sx={{ transition: "0.3s", "&:hover": { backgroundColor: "#e0f2f1", cursor: "pointer" } }}
                        />
                      );
                    })
                )}
              </Box>
            </Paper>
          </Collapse>
        )}

        {isDownloadVisible && (
          <Collapse in={showDownloadButton}>
            <Button variant="contained" onClick={handleDownload} color="secondary" fullWidth sx={{ mt: 3 }}>
              Download All
            </Button>
          </Collapse>
        )}

        <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleAlertClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert onClose={handleAlertClose} severity={alert.severity} sx={{ width: "100%" }}>
            {alert.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
}